import { z } from "zod";
import { type Author } from "../../firestore/cms/author";
import { type PodcastEpisode } from "../../firestore/cms/podcastEpisode";
import { type PodcastFeedItem } from "./podcastFeedItem";

// Define Zod schema for validating podcast episode request query parameters
export const PodcastEpisodeServerSchema = z.object({
  episodeNumber: z.string().min(1),
});

// Create a type from the Zod schema
export type PodcastEpisodeServerQueryParams = z.infer<typeof PodcastEpisodeServerSchema>;

// Define a type for the transformed episode with millisecond timestamps and image URL
export type PodcastEpisodeServerWithImageUrl = Omit<PodcastEpisode, "date" | "created_on" | "updated_on"> & {
  imageUrl: string;
  dateMillis: number;
  createdOnMillis: number;
  updatedOnMillis: number;
};

// Response type for the getPodcastEpisode endpoint
export interface PodcastEpisodeServerResponse {
  firebaseItem: PodcastEpisodeServerWithImageUrl;
  feedItem: PodcastFeedItem;
  author: Author;
}

export const PodcastEpisodesServerSchema = z.object({
  limit: z
    .string()
    .refine(
      (val) => {
        const num = Number(val);
        return Number.isInteger(num) && num >= 1 && num <= 10;
      },
      {
        message: "limit must be a string representing an integer between 1 and 10",
      },
    )
    .optional(),
  startAfterDateTimestamp: z.string().optional(),
});

// Use z.infer to create a type that matches the Zod schema
export type PodcastEpisodesServerQueryParams = z.infer<typeof PodcastEpisodesServerSchema>;

export type PodcastEpisodeServer = Omit<PodcastEpisode, "date" | "created_on" | "updated_on"> & {
  dateMillis: number;
  createdOnMillis: number;
  updatedOnMillis: number;
};

export interface PodcastEpisodesServerResponse {
  episodes: PodcastEpisodeServer[];
  pagination: {
    hasMore: boolean;
    startAfterDateTimestamp?: string;
  };
}
